import type { DyeStatus, PhotoReason, PhotoStatus } from "./photoUpdateApi";

export const REASON_LABELS: Record<PhotoReason, string> = {
  no_photo: "No photo",
  dyed_now: "Dyed now",
  colour_changed: "Colour changed",
  poor_quality: "Poor photo",
  other: "Other",
};

export const DYE_LABELS: Record<DyeStatus, string> = {
  undyed: "Undyed",
  in_dyeing: "In dyeing",
  dyed: "Dyed",
};

/** Reasons a studio admin can pick when asking for photos by hand (no_photo clears itself). */
export const MANUAL_REASONS: PhotoReason[] = ["poor_quality", "dyed_now", "colour_changed", "other"];

/** Chips in the Needs photo tab: All, then each reason. */
export const REASON_CHIPS: Array<{ key: PhotoReason | ""; label: string }> = [
  { key: "", label: "All" },
  { key: "no_photo", label: REASON_LABELS.no_photo },
  { key: "dyed_now", label: REASON_LABELS.dyed_now },
  { key: "colour_changed", label: REASON_LABELS.colour_changed },
  { key: "poor_quality", label: REASON_LABELS.poor_quality },
  { key: "other", label: REASON_LABELS.other },
];

export function openFor(days: number | null | undefined): string {
  if (days == null) return "";
  if (days <= 0) return "since today";
  return days === 1 ? "open 1 day" : `open ${days} days`;
}

/** "Dyed now · open 3 days" for the badge's second line and its accessible name. */
export function photoMeta(status: PhotoStatus | null | undefined): string {
  if (!status?.needsPhoto || !status.reason) return "";
  return [REASON_LABELS[status.reason], openFor(status.daysOpen)].filter(Boolean).join(" · ");
}

/** Badge text: uploaded photos wait for review, anything else open still needs a photo. */
export function photoBadge(status: PhotoStatus | null | undefined): { kind: "needs" | "review"; label: string; aria: string } | null {
  if (!status?.needsPhoto) return null;
  const meta = photoMeta(status);
  if (status.status === "uploaded") return { kind: "review", label: "Photos to review", aria: `Photos to review: ${meta}` };
  return { kind: "needs", label: "Needs photo", aria: `Needs photo: ${meta}` };
}

/** Line under the dye control when someone picks Dyed: what buyers will see instead of what. */
export function buyerColourLine(next: string, current: string | null | undefined): string {
  const now = (next || "").trim();
  const before = (current || "").trim();
  if (!now) return "";
  return before && before.toLowerCase() !== now.toLowerCase() ? `Buyers will see colour: ${now} (was ${before})` : `Buyers will see colour: ${now}`;
}
